// Hardcoded open positions. Mirrored by the backend job config
// (mmm-app-infra: lambda/src/shared/services/careers.ts) — slugs and field
// keys must stay identical, since the backend validates per-job with Zod.

export const CAREER_LONG_TEXT_MAX_LENGTH = 4096; // Telegram per-message limit

export type CareerFieldType = 'short' | 'long' | 'select' | 'number';

export type CareerField = {
  key: string;
  label: string;
  type: CareerFieldType;
  required: boolean;
  placeholder?: string;
  options?: string[];
  validate?: 'url';
  maxLength: number;
  // Bounds for 'number' fields; must mirror the backend Zod schema.
  min?: number;
  max?: number;
};

export type CareerJob = {
  slug: string;
  title: string;
  location: string;
  employmentType: string;
  descriptionMarkdown: string;
  fields: CareerField[];
};

const accountExecutiveDescription = `
About Yapamar:

Yapamar is a B2B SaaS platform for Marketing Mix Modeling.
We help companies accurately understand the true contribution of each marketing channel, account for external factors, and make better budget allocation decisions.

## About the role

As an Enterprise Account Executive, you'll own the full sales cycle from prospecting to closing.

## What you'll do

- Build and manage outbound pipeline in the EU market;
- Research and identify target accounts and key decision-makers;
- Conduct personalized outreach via email and LinkedIn;
- Book and run discovery meetings;
- Learn and communicate the value of Marketing Mix Modeling to marketing leaders;
- Help refine messaging, qualification criteria, and sales processes;
- Maintain CRM and keep pipeline activity organized.

## We'd love to hear from you if you

- Have experience in B2B outbound sales;
- Are comfortable building pipeline without relying on inbound leads;
- Enjoy prospecting, research, and opening conversations with senior decision-makers;
- Can work independently with minimal supervision;
- Are curious and willing to learn a technical product and a new market;
- Are familiar with marketing concepts such as ROI, ROAS, CAC, LTV, attribution, or incrementality;
- Communicate confidently in English.

## Nice to have

- Experience selling SaaS, analytics, AdTech, MarTech, or data products;
- Experience using tools such as Apollo, Sales Navigator, HubSpot, Instantly, Lemlist, or similar.

## What we offer

- Fully remote work;
- Direct collaboration with the founder and minimal bureaucracy;
- Clear path to grow with the business as it scales.
- Fixed base salary;
- Commission on closed business, including renewals.
`.trim();

export const CAREER_JOBS: CareerJob[] = [
  {
    slug: 'account-executive',
    title: 'Account Executive',
    location: 'Remote',
    employmentType: 'Full-time',
    descriptionMarkdown: accountExecutiveDescription,
    fields: [
      {
        key: 'name',
        label: 'Name',
        type: 'short',
        required: true,
        placeholder: 'First and last name',
        maxLength: 200,
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        type: 'short',
        required: true,
        placeholder: 'https://www.linkedin.com/in/your-profile',
        validate: 'url',
        maxLength: 500,
      },
      {
        key: 'earliestStart',
        label: 'When is the earliest you would want to start working with us?',
        type: 'short',
        required: true,
        maxLength: 200,
      },
      {
        key: 'desiredSalary',
        label: 'Desired base salary per month (EUR)',
        type: 'number',
        required: true,
        placeholder: 'e.g. 1500',
        maxLength: 5,
        min: 100,
        max: 10000,
      },
      {
        key: 'timeline',
        label:
          'Do you have any deadlines or timeline considerations we should be aware of?',
        type: 'long',
        required: false,
        maxLength: CAREER_LONG_TEXT_MAX_LENGTH,
      },
      {
        key: 'customersNote',
        label:
          'A short note (3–5 sentences): Which customers would you begin working with at Yapamar, and how would you start reaching them?',
        type: 'long',
        required: true,
        maxLength: CAREER_LONG_TEXT_MAX_LENGTH,
      },
      {
        key: 'coverLetter',
        label: 'Cover letter or anything else you want to share',
        type: 'long',
        required: false,
        maxLength: CAREER_LONG_TEXT_MAX_LENGTH,
      },
      {
        key: 'source',
        label: 'How did you hear about us?',
        type: 'select',
        required: true,
        options: ['LinkedIn', 'Dou', 'Djini', 'Other'],
        maxLength: 100,
      },
    ],
  },
];

export const getCareerJob = (slug?: string) =>
  CAREER_JOBS.find((job) => job.slug === slug);
